import { useTranslation } from "react-i18next"
import { Link } from "wouter"
import {
    CalendarCheck,
    CreditCard,
    Globe,
    Landmark,
    Mail,
    QrCode,
} from "lucide-react"

const CONTACT_EMAIL = "info@fohet.cz"

const STEPS = ["step1", "step2", "step3", "step4"] as const

const BENEFITS = [
    { key: "benefit1", Icon: CalendarCheck },
    { key: "benefit2", Icon: QrCode },
    { key: "benefit3", Icon: Mail },
    { key: "benefit4", Icon: CreditCard },
    { key: "benefit5", Icon: Landmark },
    { key: "benefit6", Icon: Globe },
] as const

export const ForOwnersPage = () => {
    const { t } = useTranslation()

    const mailto = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(t("forOwners.mailSubject"))}`

    return (
        <div className="space-y-16">
            {/* Hero */}
            <section className="text-center pt-8">
                <h1 className="text-4xl sm:text-5xl font-black tracking-tight text-slate-900 mb-4">
                    {t("forOwners.heroTitle")}
                </h1>
                <p className="text-slate-500 font-bold text-lg max-w-2xl mx-auto mb-8">
                    {t("forOwners.heroSubtitle")}
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                    <Link
                        to="/admin/register"
                        className="bg-brand text-white font-black rounded-[28px] px-8 py-4 shadow-xl hover:bg-brand-dark transition-colors active:scale-95"
                    >
                        {t("forOwners.ctaRegister")}
                    </Link>
                    <Link
                        to="/admin/login"
                        className="text-sm font-bold text-slate-500 hover:text-slate-700 transition-colors"
                    >
                        {t("forOwners.ctaLogin")}
                    </Link>
                </div>
            </section>

            {/* How it works */}
            <section>
                <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-slate-900 text-center mb-8">
                    {t("forOwners.howTitle")}
                </h2>
                <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
                    {STEPS.map((step, i) => (
                        <div key={step} className="bg-white rounded-[40px] border border-slate-100 shadow-xl p-6">
                            <div className="size-10 rounded-2xl bg-brand text-white font-black flex items-center justify-center mb-4">
                                {i + 1}
                            </div>
                            <h3 className="font-black text-slate-900 mb-2">
                                {t(`forOwners.${step}Title`)}
                            </h3>
                            <p className="text-sm font-bold text-slate-500">
                                {t(`forOwners.${step}Desc`)}
                            </p>
                        </div>
                    ))}
                </div>
            </section>

            {/* Benefits */}
            <section>
                <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-slate-900 text-center mb-8">
                    {t("forOwners.benefitsTitle")}
                </h2>
                <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                    {BENEFITS.map(({ key, Icon }) => (
                        <div key={key} className="bg-white rounded-[40px] border border-slate-100 shadow-xl p-6">
                            <div className="size-12 rounded-2xl bg-brand/10 text-brand flex items-center justify-center mb-4">
                                <Icon className="size-6" />
                            </div>
                            <h3 className="font-black text-slate-900 mb-2">
                                {t(`forOwners.${key}Title`)}
                            </h3>
                            <p className="text-sm font-bold text-slate-500">
                                {t(`forOwners.${key}Desc`)}
                            </p>
                        </div>
                    ))}
                </div>
            </section>

            {/* Pricing */}
            <section className="bg-white rounded-[40px] border border-slate-100 shadow-xl p-8 sm:p-12 text-center">
                <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-slate-900 mb-4">
                    {t("forOwners.pricingTitle")}
                </h2>
                <p className="text-slate-500 font-bold max-w-2xl mx-auto">
                    {t("forOwners.pricingText")}
                </p>
                <a
                    href="/legal/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block mt-6 text-sm font-black text-brand hover:text-brand-dark transition-colors"
                >
                    {t("forOwners.pricingTermsLink")}
                </a>
            </section>

            {/* Final CTA */}
            <section className="bg-charcoal rounded-[40px] shadow-xl p-8 sm:p-12 text-center">
                <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-white mb-4">
                    {t("forOwners.finalTitle")}
                </h2>
                <p className="text-slate-300 font-bold max-w-2xl mx-auto mb-8">
                    {t("forOwners.finalSubtitle")}
                </p>
                <Link
                    to="/admin/register"
                    className="inline-block bg-brand text-white font-black rounded-[28px] px-8 py-4 shadow-xl hover:bg-brand-dark transition-colors active:scale-95"
                >
                    {t("forOwners.ctaRegister")}
                </Link>
                <p className="mt-6 text-sm font-bold text-slate-400">
                    {t("forOwners.contactText")}{" "}
                    <a href={mailto} className="text-brand hover:text-brand-dark font-black">
                        {CONTACT_EMAIL}
                    </a>
                </p>
            </section>
        </div>
    )
}
